/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useMemo } from 'react';
import { formatDistance } from 'date-fns';
import { useTranslation } from 'react-i18next';

// UI lib components
import { Box, Stack, Group, Text } from '@mantine/core';

// UI local components
import User from '@ui-kit/atoms/User';
import LabelSize from '@ui-kit/atoms/LabelSize';

// Store
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import useAccount from '@features/Account/store/useAccount';

// API Types
import { User as UserType, ActivityTopic } from '@app-types/blackroom-api';
import { SectionContentProps } from '../types';

// Style
import * as classes from './styles/index.css';
import { useDateFormatter } from '@app/shared/helpers/hooks/useDateFormatter';

// Constants
const AVATAR_ICON_SIZE = 24;

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function SectionInformation({ resource }: SectionContentProps) {
  /* ********************************** HOOKS ********************************* */

  // Store
  const workspace = useWorkspace();
  const { isWorkspaceAdmin } = useAccount();

  // Localization
  const { t } = useTranslation();
  const { formatDate } = useDateFormatter();

  /* ***************************** LOCAL VARIABLES **************************** */

  const isAdmin = isWorkspaceAdmin(workspace);

  // Activities may not be available when using prefilled resource from table row
  const activities = 'activities' in resource ? resource.activities : undefined;

  const numberOfViews = (activities ?? []).filter(
    (a) => a.topic === ActivityTopic.ResourceView,
  ).length;

  const lastSeen = useMemo(() => {
    const viewActivities = (activities ?? []).filter(
      (a) => a.topic === ActivityTopic.ResourceView,
    );

    viewActivities.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    return viewActivities[0]?.createdAt;
  }, [activities]);

  /* ***************************** RENDER HELPERS ***************************** */

  function getMetadata() {
    if (isAdmin) {
      return (
        <Stack className={classes.metaDataTable} gap={0}>
          <Group>
            <Text>{t('Resource:Common.resource.viewed')}</Text>
            <Text>{`${numberOfViews} ${t('Common:quantity.times')} `}</Text>
          </Group>

          <Group>
            <Text>{t('Resource:Common.resource.lastViewedAt')}</Text>
            <Text>
              {lastSeen &&
                formatDistance(new Date(lastSeen), Date.now(), {
                  addSuffix: true,
                })}
            </Text>
          </Group>

          <Group>
            <Text>{t('Resource:Common.resource.author')}</Text>
            <User
              user={resource.createdBy as UserType}
              size={AVATAR_ICON_SIZE}
            />
          </Group>

          <Group>
            <Text>{t('Resource:Common.resource.size')}</Text>
            <LabelSize>{resource.size}</LabelSize>
          </Group>

          <Group>
            <Text>{t('Resource:Common.resource.createdAt')}</Text>
            <Text>{formatDate(resource.createdAt)}</Text>
          </Group>
        </Stack>
      );
    }
    return null;
  }

  /* ******************************** RENDERING ******************************* */
  return (
    <Box>
      <Stack gap="xs" align={!isAdmin ? 'center' : undefined}>
        {getMetadata()}
      </Stack>
    </Box>
  );
}

export default SectionInformation;
